import type { CmChecklist } from '@luxuryescapes/contract-svc-offer';

import type { ChecklistDetail } from '../../shared/checklistUtils';
import { formatBooleanDataPointValue, formatLoadedDataPointValue } from '../../shared/format';
import { findPolicyBibleRule } from '../../tabs/campaign/campaignPolicyBible';
import type { TaxesPaymentsRowView } from '../../tabs/taxesPayments/types';
import type { WithChecklistItemMetadata } from '../../types';
import { presentRowBase, warnMissingRuleText } from '../rowBase';

import { TAXES_PAYMENTS_REASON_SUMMARY, TAXES_PAYMENTS_RULE_TEXT } from './taxesPaymentsRuleText';

export function presentTaxesPaymentsRows(
  rows: Array<CmChecklist.TaxesPaymentsRow>,
): Array<WithChecklistItemMetadata<TaxesPaymentsRowView>> {
  return rows.flatMap((row) => {
    const base = presentRowBase(row);
    const ruleText = TAXES_PAYMENTS_RULE_TEXT[row.rule_id];

    if (!ruleText) {
      warnMissingRuleText(row);
      return [];
    }

    return [
      {
        ...base,
        details: presentTaxesPaymentsDetails(row.evidence),
        field: ruleText.field,
        name: ruleText.field,
        policyBibleRule:
          row.evidence.check === 'educational_package' ? findPolicyBibleRule(row.evidence.policy_bible) : undefined,
        rule: ruleText.rule,
        summary: getReasonSummary(row.reason_codes) ?? ruleText.summary[base.status],
      },
    ];
  });
}

function presentTaxesPaymentsDetails(evidence: CmChecklist.TaxesPaymentsEvidence): Array<ChecklistDetail> {
  switch (evidence.check) {
    case 'city_tax':
      return [
        { label: 'City/Other Tax Payable Direct', value: formatCityTax(evidence.city_other_tax) },
        { label: 'Details', value: evidence.details ?? 'Not provided' },
      ];
    case 'per_room_city_taxes':
      return [
        { label: 'City/Other Tax Payable Direct', value: formatCityTax(evidence.city_other_tax) },
        {
          label: 'Per-room city taxes payable at property',
          value: formatLoadedDataPointValue({
            dataAvailable: evidence.property_taxes_payable_at_property !== null,
            loaded: evidence.property_taxes_payable_at_property ?? false,
          }),
        },
      ];
    case 'payment_milestones':
      return [
        { label: 'Payment method', value: evidence.payment_method ?? 'Not provided' },
        { label: 'Milestone rules', value: evidence.payment_milestone ?? 'Not provided' },
      ];
    case 'educational_package':
      return [
        { label: 'Salesforce Educational Package', value: evidence.status ?? 'Not provided' },
        {
          label: 'Policy Bible Educational Package Clause',
          value: formatClauseEnabled(evidence.clause_enabled),
        },
        {
          label: 'Policy Bible chain / geo',
          value: evidence.policy_bible ? `${evidence.policy_bible.chain} / ${evidence.policy_bible.geo}` : 'No match',
        },
      ];
    default:
      return [];
  }
}

function formatClauseEnabled(clauseEnabled: boolean | null) {
  if (clauseEnabled === null) {
    return 'N/A';
  }

  return clauseEnabled ? 'Enabled' : 'Not enabled';
}

function getReasonSummary(reasonCodes: Array<CmChecklist.TaxesPaymentsReasonCode>) {
  return reasonCodes.map((code) => TAXES_PAYMENTS_REASON_SUMMARY[code]).find(Boolean);
}

function formatCityTax(cityOtherTax: boolean | null) {
  return formatBooleanDataPointValue({ dataAvailable: cityOtherTax !== null, enabled: cityOtherTax ?? false });
}
